import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { SyncHealthDevice, SyncHealthTenant } from '@order-pilot/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { IconCheck, IconCloudOff, IconRotate } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, Skeleton } from '@/components/States';
import {
  DEFAULT_STALE_MINUTES,
  STALE_OPTIONS,
  fetchPlatformSyncHealth,
  syncHealthKeys,
} from '@/features/sync-health/api';
import { formatCount, formatDateTime, formatRelative } from '@/lib/format';

/**
 * Live ops screen, so it refetches on a timer: an operator leaves this open on a
 * second monitor and expects "last contact" to keep moving without a reload.
 */
const REFETCH_MS = 30_000;

function StaleBadge({ device }: { device: SyncHealthDevice }) {
  if (device.revokedAt !== null) return <Badge tone="danger">Revoked</Badge>;
  if (device.lastSeenAt === null) return <Badge tone="warning">Never synced</Badge>;
  if (device.stale) return <Badge tone="warning" dot>Stale</Badge>;
  return (
    <Badge tone="success" dot>
      Syncing
    </Badge>
  );
}

/**
 * One row per DEVICE, not per tenant.
 *
 * The screen answers a single question — "which till stopped reporting?" — and
 * the unit of failure is a device, so a device is a row. The previous shape
 * interleaved tenant rows with their device rows in the same table, which gave
 * two different record types the same row shape and made a shop indistinguishable
 * from a till at a glance. Flattening also lets everything sort by urgency
 * globally: with grouping, a critical till in the third shop hides below two
 * healthy ones.
 *
 * Tenants with no device still appear, as a single row, because "no till
 * registered" is itself the thing an operator needs to see.
 */
interface DeviceRow {
  key: string;
  tenantId: string;
  tenantName: string;
  tenantStatus: SyncHealthTenant['status'];
  device: SyncHealthDevice | null;
}

function toDeviceRows(tenants: SyncHealthTenant[]): DeviceRow[] {
  const rows: DeviceRow[] = [];
  for (const tenant of tenants) {
    if (tenant.devices.length === 0) {
      rows.push({
        key: `${tenant.tenantId}:none`,
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName,
        tenantStatus: tenant.status,
        device: null,
      });
      continue;
    }
    for (const device of tenant.devices) {
      rows.push({
        key: device.deviceId,
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName,
        tenantStatus: tenant.status,
        device,
      });
    }
  }
  // Urgency first, then alphabetical so the list is stable between refreshes
  // (it reloads every 30s — rows must not dance around under the cursor).
  const rank = (row: DeviceRow): number => {
    if (row.device === null) return 0; // no till at all
    if (row.device.revokedAt !== null) return 4; // cannot sync by design
    if (row.device.lastSeenAt === null) return 1; // never synced
    if (row.device.stale) return 2;
    return 3;
  };
  return rows.sort(
    (a, b) =>
      rank(a) - rank(b) ||
      a.tenantName.localeCompare(b.tenantName) ||
      (a.device?.label ?? '').localeCompare(b.device?.label ?? ''),
  );
}

export function SyncHealthPage() {
  const [staleMinutes, setStaleMinutes] = useState(DEFAULT_STALE_MINUTES);
  const [staleOnly, setStaleOnly] = useState(false);

  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: syncHealthKeys.platform(staleMinutes),
    queryFn: ({ signal }) => fetchPlatformSyncHealth(staleMinutes, signal),
    refetchInterval: REFETCH_MS,
    // Keep the table on screen while a threshold change refetches.
    placeholderData: keepPreviousData,
  });

  const tenants = data?.tenants ?? [];
  const allRows = toDeviceRows(tenants);
  // "Needs attention" = this till cannot be relied on right now: stale, never
  // synced, or not registered at all. A revoked device is excluded on purpose —
  // it is not failing, it was switched off deliberately.
  const needsAttention = (row: DeviceRow): boolean =>
    row.device === null ||
    (row.device.revokedAt === null && (row.device.stale || row.device.lastSeenAt === null));
  const rows = staleOnly ? allRows.filter(needsAttention) : allRows;

  return (
    <>
      <PageHeader
        title="Sync health"
        description="Which tills are still reporting in — and which shop stopped syncing."
        meta={
          data ? (
            data.staleDeviceCount > 0 ? (
              <Badge tone="warning" dot>
                {formatCount(data.staleDeviceCount)} needing attention
              </Badge>
            ) : (
              <Badge tone="success" dot>
                All tills syncing
              </Badge>
            )
          ) : undefined
        }
        actions={
          <Button
            icon={<IconRotate className="h-4 w-4" />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            Refresh
          </Button>
        }
      />

      {data && data.duplicateTenantCount > 0 ? (
        <Alert tone="warning" className="mb-6">
          <p className="font-medium">
            {formatCount(data.duplicateTenantCount)}{' '}
            {data.duplicateTenantCount === 1 ? 'tenant has' : 'tenants have'} the same
            order number reported by more than one till.
          </p>
          <p className="mt-1">
            That is the signature of one till's history being re-uploaded under a second
            device — most often a replaced machine registered as a new till while its POS
            database was restored from a backup. The rows are all valid, so nothing errors,
            but every figure that sums across tills is inflated for the affected dates. To
            move a till to new hardware, rotate its token instead of creating a second
            device.
          </p>
        </Alert>
      ) : null}

      {data ? (
        <dl className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Tenants', value: formatCount(data.tenantCount) },
            { label: 'Devices', value: formatCount(data.deviceCount) },
            {
              label: 'Stale devices',
              value: formatCount(data.staleDeviceCount),
              tone: data.staleDeviceCount > 0,
            },
            { label: 'Rows synced', value: formatCount(data.totalRowCount) },
          ].map((tile) => (
            <div key={tile.label} className="card px-5 py-4">
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {tile.label}
              </dt>
              <dd
                className={`mt-1 text-2xl font-semibold tabular-nums ${
                  tile.tone === true ? 'text-amber-700' : 'text-slate-900'
                }`}
              >
                {tile.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3.5 sm:px-5">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label
                htmlFor="stale-threshold"
                className="text-xs font-medium uppercase tracking-wider text-slate-500"
              >
                Stale after
              </label>
              <select
                id="stale-threshold"
                className="input h-8 w-auto py-0 pr-8 text-xs"
                value={staleMinutes}
                onChange={(event) => setStaleMinutes(Number(event.target.value))}
              >
                {STALE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                checked={staleOnly}
                onChange={(event) => setStaleOnly(event.target.checked)}
              />
              Needs attention only
            </label>
          </div>
          <p className="text-xs text-slate-500" aria-live="polite">
            {isPending
              ? 'Loading…'
              : // Only ever rendered from a real fetch: dataUpdatedAt is 0 until the
                // first success, which would read as "56 years ago" after an error.
                data !== undefined
                ? `Updated ${formatRelative(new Date(dataUpdatedAt).toISOString(), 'just now')}`
                : ''}
            {!isPending && isFetching ? ' · refreshing' : ''}
          </p>
        </div>

        {isPending ? (
          <div className="divide-y divide-slate-100">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center gap-4 px-5 py-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-44" />
                  <Skeleton className="h-2.5 w-28" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="hidden h-3.5 w-24 sm:block" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            error={error}
            title="Could not load sync health"
            onRetry={() => void refetch()}
            retrying={isFetching}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={staleOnly ? <IconCheck className="h-5 w-5" /> : <IconCloudOff className="h-5 w-5" />}
            title={staleOnly ? 'Every till is reporting in' : 'No devices yet'}
            description={
              staleOnly
                ? `No device has been out of contact for more than ${staleMinutes} minutes.`
                : 'Register a restaurant and issue its till a sync token to see it here.'
            }
            action={
              staleOnly ? (
                <Button onClick={() => setStaleOnly(false)}>Show all devices</Button>
              ) : (
                <Link to="/cp/tenants" className="btn btn-md btn-secondary">
                  Go to tenants
                </Link>
              )
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">
                Sync devices across all tenants, the ones needing attention first
              </caption>
              <thead className="bg-surface">
                <tr>
                  <th scope="col" className="th w-8">
                    <span className="sr-only">Attention</span>
                  </th>
                  <th scope="col" className="th">
                    Device
                  </th>
                  <th scope="col" className="th">
                    Restaurant
                  </th>
                  <th scope="col" className="th">
                    Branch
                  </th>
                  <th scope="col" className="th">
                    Status
                  </th>
                  <th scope="col" className="th">
                    Last contact
                  </th>
                  <th scope="col" className="th text-right">
                    Rows
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => {
                  const flagged = needsAttention(row);
                  return (
                    <tr key={row.key} className={flagged ? 'bg-amber-50/60' : undefined}>
                      <td className="td text-center">
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${
                            flagged
                              ? 'bg-amber-500'
                              : row.device?.revokedAt !== null && row.device !== null
                                ? 'bg-slate-300'
                                : 'bg-emerald-500'
                          }`}
                          title={flagged ? 'Needs attention' : 'Syncing'}
                        />
                      </td>

                      <th scope="row" className="td text-left">
                        {row.device === null ? (
                          <span className="font-medium text-slate-500">No device registered</span>
                        ) : (
                          <>
                            <p className="font-semibold text-slate-900">{row.device.label}</p>
                            <p className="mt-0.5 font-mono text-xs font-normal text-slate-400">
                              {row.device.tokenPrefix}…
                            </p>
                          </>
                        )}
                      </th>

                      {/* The tenant is a column, not a parent row: one glance
                          answers "whose till is this?" without reading upwards. */}
                      <td className="td">
                        <Link
                          to={`/cp/tenants/${row.tenantId}`}
                          className="rounded font-medium text-slate-700 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
                        >
                          {row.tenantName}
                        </Link>
                        {row.tenantStatus === 'DISABLED' ? (
                          <span className="ml-1.5 align-middle">
                            <Badge tone="neutral">Disabled</Badge>
                          </span>
                        ) : null}
                      </td>

                      <td className="td text-slate-600">
                        {row.device?.branchName ?? <span className="text-slate-400">—</span>}
                      </td>

                      <td className="td">
                        {row.device === null ? (
                          <Badge tone="warning">Nothing can sync</Badge>
                        ) : (
                          <StaleBadge device={row.device} />
                        )}
                      </td>

                      <td
                        className="td whitespace-nowrap"
                        title={row.device ? formatDateTime(row.device.lastSeenAt) : undefined}
                      >
                        <span className={flagged ? 'font-medium text-amber-800' : 'text-slate-600'}>
                          {row.device ? formatRelative(row.device.lastSeenAt) : '—'}
                        </span>
                      </td>

                      <td className="td whitespace-nowrap text-right tabular-nums text-slate-600">
                        {row.device ? formatCount(row.device.rowCount) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data ? (
          <div className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500 sm:px-5">
            A device counts as stale after {data.staleAfterMinutes} minutes without contact.
            Revoked devices are never flagged — they cannot sync by design.
          </div>
        ) : null}
      </div>
    </>
  );
}
